import { FC } from 'react'
import styled, { CSSProperties } from 'styled-components'
import { useCountWishlist } from '@/apis/rest/generated/hooks'
import ScrollContainer from '@/components/domains/etc/ScrollContainer'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { MinCountForScrollContainerEnum } from '@/constants/min-count-for-scroll-container.enum'
import { ProductLocation } from '@/constants/product-location.const'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import useCartEventTracker from '@/containers/event-tracker/cart.event-tracker'
import useIntersectionObserver from '@/containers/hooks/useIntersectionObserver'
import { GraphQLProductType, transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'

interface ListProductSliderProps {
  style?: CSSProperties
  products?: GraphQLProductType[] // 상품 리스트
  isLoading: boolean // 로딩 중일 경우 스켈레톤을 보여주기 위한 boolean
  sectionName?: string // 섹션 타이틀
  onTrackView?: () => void
  addToCartCallback?: (ids?: number[]) => void
}

const ListProductSliderV2: FC<ListProductSliderProps> = ({
  isLoading,
  style,
  products = [],
  onTrackView,
  sectionName = '',
  addToCartCallback,
}) => {
  const { refetch: refetchWishCount } = useCountWishlist({
    query: {
      enabled: false,
    },
  })
  const { elementRef } = useIntersectionObserver(
    () => {
      onTrackView?.()
    },
    { rootMargin: '0px' }
  )

  const handleClickLike = () => {
    refetchWishCount()
  }

  const { trackClickCartBottomProductEvent } = useCartEventTracker()

  const trackClickProduct = (idx: number, product: GraphQLProductType) => {
    trackClickCartBottomProductEvent({
      listIndex: idx,
      sectionIndex: 0,
      sectionName: sectionName,
      productId: product.id,
      productName: product.name,
      productPurchasePrice: product.purchasePrice,
      productPrice: product.price,
      productDiscountRate: product.discountRate,
      productReviewAvgScore: product.averageReviewScore,
      productReviewTotalCount: product.reviewCount,
    })
  }

  return (
    <ProductListWrapper ref={elementRef} className="product-list" style={style}>
      <ScrollContainer
        hideRightButton={products.length < MinCountForScrollContainerEnum.CartProductListSlider}
        buttonPositionY={30}
      >
        <div className="product-list__list">
          {isLoading && <SkeletonProduct size={ProductThumbnailSizeEnum.Card136} />}
          {products?.map((product, index) => {
            return (
              <div className="product-list__item" key={product.id}>
                <NewProductCard
                  product={transformProductTypeDefinedByFrontend(product)}
                  size={PRODUCT_CARD_SIZES.lg}
                  onClickProductCard={() => trackClickProduct(index, product)}
                  hasCartButton
                  callbackAddToCart={addToCartCallback}
                  callbackLike={handleClickLike}
                  trackerData={{
                    addLocation: ProductLocation.CartBottom,
                    sectionName: sectionName,
                    sectionIndex: 0,
                    listIndex: index,
                  }}
                />
              </div>
            )
          })}
        </div>
      </ScrollContainer>
    </ProductListWrapper>
  )
}

const ProductListWrapper = styled.div`
  .product-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.6rem;
    }
    &__link {
      border: none;
      padding: 0;
      background-color: transparent;
      outline: none;
      cursor: pointer;
      display: flex;
      align-items: center;
    }
    &__content {
    }
    &__list {
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 0.8rem;
      & > *:first-child {
        padding-left: 1.6rem;
      }
      & > *:last-child {
        padding-right: 1.6rem;
      }
    }
    &__item {
    }
  }
`
export default ListProductSliderV2
